// cn(): Klassen zusammenführen (DESIGN §3.0). Behelf, bis `tailwind-merge` installiert ist
// (UI-Wunsch ui-basis): clsx + eine kleine Konfliktauflösung für die Utility-Gruppen von Bar.
// Spätere Klassen gewinnen innerhalb derselben Gruppe und derselben Varianten-Präfixe
// (`hover:`, `md:` …). Gruppen ohne Eintrag bleiben unangetastet (lieber doppelt als falsch).
import { type ClassValue, clsx } from 'clsx';
import { COLOR_NAMES, TEXT_ROLES } from './tokens';

export type { ClassValue };

const COLORS = new Set<string>(COLOR_NAMES);
const TEXT_SIZES = new Set<string>(TEXT_ROLES);
const DISPLAY = new Set([
  'block',
  'inline-block',
  'inline',
  'flex',
  'inline-flex',
  'grid',
  'inline-grid',
  'hidden',
  'contents',
]);
const POSITION = new Set(['static', 'fixed', 'absolute', 'relative', 'sticky']);
const WEIGHTS = new Set(['font-normal', 'font-medium', 'font-semibold', 'font-bold']);
const COLOR_PREFIXES = new Set([
  'bg',
  'text',
  'border',
  'ring',
  'outline',
  'fill',
  'stroke',
  'from',
  'to',
]);
/** Längere Präfixe zuerst, damit `min-w` nicht als `min` erkannt wird. */
const PREFIXES = [
  'inset-x',
  'inset-y',
  'gap-x',
  'gap-y',
  'min-w',
  'min-h',
  'max-w',
  'max-h',
  'rounded',
  'opacity',
  'shadow',
  'bottom',
  'border',
  'outline',
  'stroke',
  'inset',
  'right',
  'left',
  'size',
  'fill',
  'ring',
  'from',
  'text',
  'gap',
  'top',
  'bg',
  'to',
  'px',
  'py',
  'pt',
  'pr',
  'pb',
  'pl',
  'mx',
  'my',
  'mt',
  'mr',
  'mb',
  'ml',
  'p',
  'm',
  'w',
  'h',
  'z',
];

/** Wert ohne Deckkraft-Suffix (`bg-card/50` → `card`). */
const colorOf = (value: string) => value.split('/')[0] ?? value;

/** Gruppe einer Utility ohne Varianten, oder null, wenn unbekannt. */
export function utilityGroup(utility: string): string | null {
  const u = utility.startsWith('!') ? utility.slice(1) : utility;
  if (DISPLAY.has(u)) return 'display';
  if (POSITION.has(u)) return 'position';
  if (WEIGHTS.has(u)) return 'font-weight';
  const bare = u.startsWith('-') ? u.slice(1) : u;
  const prefix = PREFIXES.find((p) => bare.startsWith(`${p}-`));
  if (!prefix) return null;
  const value = bare.slice(prefix.length + 1);
  if (COLOR_PREFIXES.has(prefix)) {
    if (COLORS.has(colorOf(value))) return `${prefix}-color`;
    if (prefix === 'text' && TEXT_SIZES.has(value)) return 'text-size';
    return null;
  }
  if (prefix === 'rounded') {
    const side = /^(t|b|l|r|tl|tr|bl|br|s|e)-/.exec(value);
    return side ? `rounded-${side[1]}` : 'rounded';
  }
  return prefix;
}

function groupKey(cls: string): string | null {
  const parts = cls.split(':');
  const utility = parts.pop() ?? '';
  if (utility === 'rounded') return `${parts.join(':')}|rounded`;
  const group = utilityGroup(utility);
  return group ? `${parts.join(':')}|${group}` : null;
}

/** Klassen zusammenführen; bei Konflikten gewinnt die spätere Klasse. */
export function cn(...inputs: ClassValue[]): string {
  const list = clsx(inputs).split(/\s+/).filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const cls = list[i]!;
    const key = groupKey(cls) ?? `#${cls}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(cls);
  }
  return out.reverse().join(' ');
}
